import { useMemo } from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm, UseFormReturn } from "react-hook-form";

import {
  employmentStatusEnumSchema,
  payFrequencyEnumSchema,
} from "@libs/api/patients";
import {
  employedFormSchema,
  EmployedFormType,
  HouseholdMemberType,
  selfEmployedFormSchema,
  SelfEmployedFormType,
  unemployedFormSchema,
  UnemployedFormType,
} from "@/domain/householdMember";

type UseWorkAndIncomeFormsReturnType = {
  employedForm: UseFormReturn<EmployedFormType>;
  unemployedForm: UseFormReturn<UnemployedFormType>;
  selfEmployedForm: UseFormReturn<SelfEmployedFormType>;
};

export const useWorkAndIncomeForms = (
  householdMember: HouseholdMemberType,
): UseWorkAndIncomeFormsReturnType => {
  const employedFormDefaultValues = useMemo(
    () => ({
      employmentStatus:
        householdMember.employmentStatus ??
        employmentStatusEnumSchema.enum.EMPLOYED,
      grossMonthlyIncome:
        householdMember.grossMonthlyIncome != null
          ? String(householdMember.grossMonthlyIncome)
          : "",
      workWeekHours: householdMember.workWeekHours ?? "40",
      payFrequency:
        householdMember.payFrequency ?? payFrequencyEnumSchema.enum.BI_WEEKLY,
      employerName: householdMember.employerName ?? "",
    }),
    [householdMember],
  );

  const unemployedFormDefaultValues = useMemo(
    () => ({
      employmentStatus: employmentStatusEnumSchema.enum.UNEMPLOYED,
      unemploymentCompensationStatus:
        householdMember.unemploymentCompensationStatus ?? null,
      unemploymentCompensationAmount:
        householdMember.unemploymentCompensationAmount
          ? String(householdMember.unemploymentCompensationAmount)
          : null,
    }),
    [householdMember],
  );

  const selfEmployedFormDefaultValues = useMemo(
    () => ({
      employmentStatus: employmentStatusEnumSchema.enum.SELF_EMPLOYED,
      employmentStatusSelfEmployedDetails:
        householdMember.employmentStatusSelfEmployedDetails ?? "",
      selfEmploymentNetMonthlyIncome:
        householdMember.selfEmploymentNetMonthlyIncome != null
          ? String(householdMember.selfEmploymentNetMonthlyIncome)
          : "",
    }),
    [householdMember],
  );

  const employedForm = useForm<EmployedFormType>({
    defaultValues: employedFormDefaultValues,
    resolver: standardSchemaResolver(employedFormSchema),
    mode: "onChange",
  });

  const unemployedForm = useForm<UnemployedFormType>({
    defaultValues: unemployedFormDefaultValues,
    resolver: standardSchemaResolver(unemployedFormSchema),
    mode: "onChange",
  });

  const selfEmployedForm = useForm<SelfEmployedFormType>({
    defaultValues: selfEmployedFormDefaultValues,
    resolver: standardSchemaResolver(selfEmployedFormSchema),
    mode: "onChange",
  });

  return {
    employedForm,
    unemployedForm,
    selfEmployedForm,
  };
};
